import { useEffect, useState } from "react";
import { http, money, mesi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Info } from "lucide-react";

const now = new Date();

const COLS = [
  { k: "total_net", l: "Ricavi", strong: true },
  { k: "owner_result", l: "Netto proprietario", strong: true },
  { k: "management_fee", l: "Provvigione PuraVida", strong: true },
  { k: "pulizie", l: "Pulizie" },
  { k: "lavanderia", l: "Lavanderia" },
  { k: "manutenzioni", l: "Manutenzioni" },
  { k: "consumabili", l: "Consumabili" },
  { k: "amenities", l: "Amenities" },
  { k: "altri", l: "Altri costi" },
];

function Chart({ title, data, dataKey, color, testid }) {
  return (
    <Card className="p-4 rounded-2xl" data-testid={testid}>
      <h3 className="font-display text-base md:text-lg font-bold text-slate-900 mb-3">{title}</h3>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer minWidth={0} minHeight={220} debounce={50}>
          <BarChart data={data} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EDF3F3" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
            <Bar dataKey={dataKey} name={title} fill={color} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export default function AnalisiEconomica() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [apartmentId, setApartmentId] = useState("all");
  const [apartments, setApartments] = useState([]);
  const [data, setData] = useState(null);
  const [an, setAn] = useState(null);
  const [view, setView] = useState("mese");

  useEffect(() => { http.get("/apartments").then((r) => setApartments(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = { year, month };
    if (apartmentId !== "all") params.apartment_id = apartmentId;
    http.get("/economics/summary", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [year, month, apartmentId]);
  useEffect(() => {
    const params = { year };
    if (view === "mese") params.month = month;
    if (apartmentId !== "all") params.apartment_id = apartmentId;
    http.get("/finance/analysis", { params }).then((r) => setAn(r.data)).catch(() => {});
  }, [year, month, apartmentId, view]);

  const rows = view === "mese" ? (data?.month_rows || []) : (data?.months || []).filter((m) => m.reports > 0).map((m) => ({ ...m, apartment_name: `${mesi[m.month]} ${year}`, apartment_id: `m${m.month}` }));
  const totals = view === "mese" ? data?.month_totals : data?.year_totals;
  const series = (data?.months || []).filter((m) => m.reports > 0);
  const byApt = (data?.year_rows || []).map((r) => ({ ...r, label: r.apartment_name }));
  const mg = an?.margine_pulizie; const rs = an?.risultato;
  const periodLabel = view === "mese" ? `${mesi[month]} ${year}` : `anno ${year}`;

  return (
    <div className="space-y-6" data-testid="analisi-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--teal-medium)] font-semibold">Quanto produce la gestione PuraVida</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Analisi economica</h1>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> Competenza economica dai rendiconti finalizzati (bozze escluse). Non è cassa: per gli incassi vedi Incassi &amp; Pagamenti.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}><SelectTrigger className="w-[140px]" data-testid="ent-month"><SelectValue /></SelectTrigger>
            <SelectContent>{mesi.slice(1).map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}><SelectTrigger className="w-[110px]" data-testid="ent-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
          <Select value={apartmentId} onValueChange={setApartmentId}><SelectTrigger className="w-[200px]" data-testid="ent-apt"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti gli appartamenti</SelectItem>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4 rounded-2xl border-0 text-white" style={{ background: "linear-gradient(135deg,#2E5C60 0%,#3F7B7C 100%)" }} data-testid="ent-fee-month">
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--pv-mint-soft)] font-semibold">Provvigioni {mesi[month]}</div>
          <div className="font-display text-2xl md:text-3xl font-extrabold tabular-nums mt-1">{money(data?.month_totals?.management_fee)}</div>
        </Card>
        <Card className="p-4 rounded-2xl" data-testid="ent-fee-ytd">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Provvigioni {year} (fino a {mesi[month]})</div>
          <div className="font-display text-2xl md:text-3xl font-extrabold text-[var(--teal-deep)] tabular-nums mt-1">{money(data?.ytd_totals?.management_fee)}</div>
        </Card>
        <Card className="p-4 rounded-2xl" data-testid="ent-revenue-month">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ricavi {mesi[month]}</div>
          <div className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 tabular-nums mt-1">{money(data?.month_totals?.total_net)}</div>
        </Card>
        <Card className="p-4 rounded-2xl" data-testid="ent-reports">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Rendiconti finalizzati</div>
          <div className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 tabular-nums mt-1">{data?.month_totals?.reports ?? 0}<span className="text-sm text-slate-400 font-semibold"> / {data?.year_totals?.reports ?? 0} anno</span></div>
        </Card>
      </div>

      <div className="flex gap-2">
        {[{ k: "mese", l: `Mensile · ${mesi[month]} ${year}` }, { k: "anno", l: `Annuale · ${year}` }].map((t) => (
          <button key={t.k} data-testid={`ent-view-${t.k}`} onClick={() => setView(t.k)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border min-h-[44px] transition ${view === t.k ? "bg-[var(--teal-deep)] text-white border-[var(--teal-deep)]" : "bg-white text-slate-600 border-slate-200 hover:border-[var(--pv-mint)]"}`}>{t.l}</button>
        ))}
      </div>

      <Card className="rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ent-table">
            <thead><tr className="text-left border-b border-slate-200 text-[11px] uppercase text-slate-500">
              <th className="py-3 px-4">{view === "mese" ? "Appartamento" : "Mese"}</th>{view === "mese" && <th className="px-2">Stato</th>}
              {COLS.map((c) => <th key={c.k} className="px-2 text-right whitespace-nowrap">{c.l}</th>)}
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={COLS.length + 2} className="py-10 text-center text-slate-400">Nessun rendiconto finalizzato nel periodo.</td></tr>}
              {rows.map((r) => (
                <tr key={r.report_id || r.apartment_id} className="border-b border-slate-100 hover:bg-[color:var(--teal-light)]/60" data-testid={`ent-row-${r.apartment_id}`}>
                  <td className="py-3 px-4 font-medium whitespace-nowrap">{r.apartment_name}{r.owner_name && <div className="text-xs text-slate-400">{r.owner_name}</div>}</td>
                  {view === "mese" && <td className="px-2"><Badge variant="outline" className="border-[var(--pv-mint)] text-[var(--teal-deep)] capitalize">{r.status}</Badge></td>}
                  {COLS.map((c) => <td key={c.k} className={`px-2 text-right tabular-nums whitespace-nowrap ${c.strong ? "font-bold text-[var(--teal-deep)]" : ""}`}>{money(r[c.k])}</td>)}
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="bg-[color:var(--teal-light)] font-bold" data-testid="ent-totals">
                  <td className="py-3 px-4">TOTALE {view === "mese" ? "MESE" : "ANNO"}</td>{view === "mese" && <td />}
                  {COLS.map((c) => <td key={c.k} className="px-2 text-right tabular-nums whitespace-nowrap">{money(totals?.[c.k])}</td>)}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 rounded-2xl" data-testid="margine-pulizie-card">
          <div className="text-xs uppercase tracking-widest text-[var(--teal-medium)] font-semibold">Approfondimento · {periodLabel}</div>
          <h3 className="font-display text-base md:text-lg font-bold text-slate-900 mb-3">Margine pulizie PuraVida</h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span>Pulizie fatturate ai proprietari <span className="text-slate-400 text-xs">(rendiconti)</span></span><b className="tabular-nums" data-testid="mg-billed">{money(mg?.pulizie_fatturate)}</b></div>
            <div className="flex justify-between"><span>Compensi reali operatrici <span className="text-slate-400 text-xs">(registro · {mg?.interventi ?? 0} interventi)</span></span><b className="tabular-nums" data-testid="mg-fees">- {money(mg?.compensi_operatrici)}</b></div>
            <div className="flex justify-between border-t border-slate-200 pt-2 text-base"><span className="font-semibold">Margine pulizie</span><b className={`tabular-nums ${(mg?.margine || 0) < 0 ? "text-rose-700" : "text-[var(--teal-deep)]"}`} data-testid="mg-margin">{money(mg?.margine)}</b></div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Analisi interna: non modifica i rendiconti.</p>
        </Card>
        <Card className="p-4 rounded-2xl" data-testid="risultato-card">
          <div className="text-xs uppercase tracking-widest text-[var(--teal-medium)] font-semibold">Marginalità · {periodLabel}</div>
          <h3 className="font-display text-base md:text-lg font-bold text-slate-900 mb-3">Risultato PuraVida</h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span>Gestione PuraVida <span className="text-slate-400 text-xs">(provvigioni dai rendiconti finalizzati)</span></span><b className="tabular-nums" data-testid="rs-gestione">{money(rs?.gestione_puravida)}</b></div>
            <div className="flex justify-between"><span>Altri ricavi PuraVida <span className="text-slate-400 text-xs">(senza appartamento / extra)</span></span><b className="tabular-nums" data-testid="rs-altri">{money(rs?.altri_ricavi_puravida)}</b></div>
            <div className="flex justify-between border-t border-slate-200 pt-2"><span className="font-semibold">Totale PuraVida</span><b className="tabular-nums text-[var(--teal-deep)]" data-testid="rs-totale">{money(rs?.totale_puravida)}</b></div>
            <div className="pt-2 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Marginalità</div>
            <div className="flex justify-between"><span>Margine pulizie</span><b className="tabular-nums">{money(rs?.margine_pulizie)}</b></div>
            <div className="flex justify-between"><span>Costi realmente PuraVida</span><b className="tabular-nums">- {money(rs?.costi_puravida)}</b></div>
            <div className="flex justify-between border-t border-slate-200 pt-2 text-base"><span className="font-semibold">Risultato</span><b className="tabular-nums text-[var(--teal-deep)]" data-testid="rs-result">{money(rs?.risultato)}</b></div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">{rs?.nota}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Chart title="Provvigioni PuraVida" data={series} dataKey="management_fee" color="#2E5C60" testid="chart-fee" />
        <Chart title="Ricavi" data={series} dataKey="total_net" color="#8FC0B8" testid="chart-revenue" />
        <Chart title="Netto proprietari" data={series} dataKey="owner_result" color="#E07A5F" testid="chart-owner" />
      </div>
      {byApt.length > 1 && <Chart title={`Provvigioni per appartamento · ${year}`} data={byApt} dataKey="management_fee" color="#3F7B7C" testid="chart-apt" />}
    </div>
  );
}
